import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fetchProducts } from "@/lib/api";
import { MOODS, PRIMARY_NOTES, GENDERS } from "@/lib/config";
import { Link } from "react-router-dom";
import { useStore } from "@/lib/store";

const TABS = [
  { key: "mood", label: "By Mood", options: MOODS.map((m) => ({ value: m.key, label: m.label })) },
  { key: "note", label: "By Note", options: PRIMARY_NOTES.map((n) => ({ value: n, label: n })) },
  { key: "gender", label: "By Character", options: GENDERS.map((g) => ({ value: g.key, label: g.label })) },
];

export function ScentFinderBar({ variant = "light" }) {
  const dark = variant === "dark";
  const { format } = useStore();
  const [tab, setTab] = useState("mood");
  const [value, setValue] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = {};
    if (value) params[tab] = value;
    setLoading(true);
    fetchProducts(params)
      .then((data) => setItems((data || []).slice(0, 6)))
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[scent-finder] fetchProducts failed", err?.response?.status);
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [tab, value]);

  const currentTab = useMemo(() => TABS.find((t) => t.key === tab), [tab]);

  const textPrimary = dark ? "text-[#FAF8F5]" : "text-[#1A1918]";
  const textMuted = dark ? "text-[#FAF8F5]/60" : "text-[#1A1918]/60";
  const chipBg = dark ? "bg-white/5" : "bg-[#FAF8F5]";
  const chipBorder = dark ? "border-white/12" : "border-black/10";
  const chipText = dark ? "text-[#FAF8F5]/80" : "text-[#1A1918]/80";
  const chipHover = dark ? "hover:border-[#D4B278]/60" : "hover:border-[#C5A059]/40";
  const cardBg = dark ? "bg-[#141210]" : "bg-[#F1ECE4]";
  const cardBorder = dark ? "border-white/10" : "border-black/10";

  return (
    <div data-testid="scent-finder-bar" className="mt-10 sm:mt-14">
      <div className="flex items-center gap-4 sm:gap-6 overflow-x-auto pb-1 -mx-1 px-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`scent-finder-tab-${t.key}`}
            onClick={() => { setTab(t.key); setValue(null); }}
            className={`whitespace-nowrap text-[11px] sm:text-xs uppercase tracking-[0.16em] sm:tracking-[0.18em] pb-2 transition-colors ${
              tab === t.key ? `${textPrimary} border-b-2 border-[#D4B278]` : `${textMuted} hover:${textPrimary}`
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="mt-4 sm:mt-5 flex flex-wrap gap-2">
        <button
          onClick={() => setValue(null)}
          data-testid="scent-finder-chip-all"
          className={`px-3.5 py-1.5 rounded-full text-xs border transition-colors ${
            !value
              ? (dark ? "bg-[#FAF8F5] text-[#0B0A08] border-[#FAF8F5]" : "bg-[#1A1918] text-[#FAF8F5] border-[#1A1918]")
              : `${chipBg} ${chipText} ${chipBorder} ${chipHover}`
          }`}
        >All</button>
        {currentTab?.options.map((o) => (
          <button
            key={o.value}
            onClick={() => setValue(o.value)}
            data-testid={`scent-finder-chip-${o.value.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
            className={`px-3.5 py-1.5 rounded-full text-xs border transition-colors ${
              value === o.value
                ? "bg-[#C5A059]/20 border-[#D4B278] text-[#D4B278]"
                : `${chipBg} ${chipText} ${chipBorder} ${chipHover}`
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>

      <div className="mt-6 sm:mt-8 min-h-[240px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${tab}-${value}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4"
            data-testid="scent-finder-results"
          >
            {loading && items.length === 0
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div key={`scent-finder-skel-${i}`} className={`aspect-[3/4] rounded-xl ${dark ? 'bg-white/5' : 'sd-skeleton'}`} />
                ))
              : items.map((p) => (
                  <Link
                    key={p.id}
                    to={`/product/${p.slug}`}
                    data-testid={`scent-finder-result-${p.slug}`}
                    className={`group rounded-xl border ${cardBorder} ${cardBg} overflow-hidden hover:border-[#D4B278]/50 transition-colors`}
                  >
                    <div
                      className="aspect-[3/4] relative overflow-hidden"
                      style={{ backgroundColor: dark ? "#F1ECE4" : "#FAF8F5" }}
                    >
                      {/* Soft warm ivory frame — image sits naturally on it, colours fully preserved */}
                      <div
                        className="absolute inset-0 transition-transform duration-500 group-hover:scale-[1.04]"
                        style={{
                          backgroundImage: `url(${p.image_url})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      />
                      {/* Subtle vignette to help the tile blend into the dark section without desaturating the photo */}
                      {dark && (
                        <div
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            background:
                              "radial-gradient(ellipse at center, transparent 55%, rgba(11,10,8,0.35) 100%)",
                          }}
                        />
                      )}
                    </div>
                    <div className="p-3">
                      <div className={`text-[10px] uppercase tracking-[0.16em] ${textMuted}`}>{p.number}</div>
                      <div className={`font-serif text-base mt-0.5 group-hover:text-[#D4B278] transition-colors ${textPrimary}`}>{p.name}</div>
                      <div className={`text-xs mt-0.5 ${dark ? 'text-[#FAF8F5]/60' : 'text-[#1A1918]/70'}`}>{format(p.sizes[0].price_usd)}</div>
                    </div>
                  </Link>
                ))}
            {!loading && items.length === 0 && (
              <div className={`col-span-full text-center py-10 text-sm ${textMuted}`}>
                No fragrances match that filter yet. Try another selection.
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default ScentFinderBar;
